import * as Icons from "@mui/icons-material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import InboxIcon from "@mui/icons-material/Inbox";
import LocalShipping from "@mui/icons-material/LocalShipping";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import { useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import AvatarWithStatus from "@/components/AvatarWithStatus.tsx";
import FirstLocationRedirectHook from "@/components/FirstLocationRedirectHook.tsx";
import ChannelDial from "@/components/Icons/ChannelDial.tsx";
import ManageUsers from "@/components/Icons/ManageUsers";
import TemplateIcon from "@/components/Icons/TemplateIcon.tsx";
import LanguageDropButton from "@/components/LanguageDropButton.tsx";
import ReadOnlyModeToggle from "@/components/ReadOnlyModeToggle.tsx";
import ToggleSecondWindowButton from "@/components/SecondWindow/ToggleSecondWindowButton.tsx";
import { ampli } from "@/config";
import { useDispatchEnabled } from "@/hooks/dispatch/useDispatchEnabled";
import UseFirebase from "@/hooks/firebase/useFirebase";
import { useGlobalFlags } from "@/hooks/useGlobalFlags.ts";
import { useSafeLocalStorage } from "@/hooks/useSafeLocalStorage";
import { useTeamAliases } from "@/hooks/useTeamAliases";
import {
  channelUnreadCountCollections,
  computeChannelUnreadCount,
  computeDriverUnreadCount,
  driverUnreadCountCollections,
} from "@/models/repositories";
import { MyAccountContext } from "@/models/StateProviders/myAccountProvider.tsx";
import { WorkspaceContext } from "@/models/StateProviders/workspaceProvider.tsx";
import { UxContext } from "@/models/UxStateProvider.tsx";
import { useCollectionsCompute } from "@/store/useCollectionsCompute";
import type { VerticalLinkItem } from "@/ui/VerticalLinks";
import { ipcRendererSend, isElectron } from "@/utils";

export type LeftSidebarNavState = {
  topLinks: VerticalLinkItem[];
  bottomLinks: VerticalLinkItem[];
  helpOpen: boolean;
  closeHelp: () => void;
  notificationsDeniedOpen: boolean;
  closeNotificationsDenied: () => void;
};

/**
 * Owns everything behind the left icon rail: unread-count queries, first-location links, the
 * channels-nav visibility cache, flag-gated tools, and the dialog open/close state. The paired
 * pure view is {@link SidebarNav}; {@link LeftSidebar} wires the two plus the dialogs.
 */
export function useLeftSidebarNav(): LeftSidebarNavState {
  const { setGlobalSearchOpen } = useContext(UxContext);
  const { isWorkspaceAdmin, isWorkspaceLimitedMember, myCurrentWorkspaceMembership } = useContext(WorkspaceContext);
  const { myAccount } = useContext(MyAccountContext);
  const { workspaceId, subPage } = useParams();
  const workspaceAdmin = isWorkspaceAdmin();
  const limitedMember = isWorkspaceLimitedMember();
  const { readOnlyMode } = useGlobalFlags();
  const [readOnlyActive, setReadOnlyActive] = useState<boolean>(false);
  const [helpOpen, setHelpOpen] = useState<boolean>(false);
  const { notificationsState, requestDesktopNotifications } = UseFirebase();
  const [notificationsDeniedOpen, setNotificationsDeniedOpen] = useState(false);

  const { redirectUrl, list: channelListForNav } = FirstLocationRedirectHook();
  const driversLink = redirectUrl("drivers");
  const channelsLink = redirectUrl("channels");

  // Persist per workspace so the icon stays put across route changes that remount the sidebar
  // (e.g. drivers -> channels -> drivers). Without this cache, the channels icon flashes back in
  // each time because the reactive query takes a tick to repopulate after remount.
  const channelsNavItemCacheKey = workspaceId ? `showChannelsNavItem:${workspaceId}` : "showChannelsNavItem:none";
  const [showChannelsNavItem, setShowChannelsNavItem] = useSafeLocalStorage<boolean>(channelsNavItemCacheKey, false);

  useEffect(() => {
    if (channelListForNav == null || workspaceId == null) return;
    const inWorkspace = channelListForNav.filter((c) => c.workspaceId === workspaceId);
    if (inWorkspace.length === 0 && channelListForNav.length > 0) {
      return;
    }
    setShowChannelsNavItem(
      inWorkspace.some((c) => !c.aliasChannel && c.id && c.workspaceId && c.enabled && c.name === "read"),
    );
  }, [channelListForNav, workspaceId, setShowChannelsNavItem]);

  const pathName = window.location.pathname;
  const workspaceMembershipId = myCurrentWorkspaceMembership?.id;
  const { teamAliases } = useTeamAliases(workspaceId ?? "", workspaceMembershipId ?? "");

  const teamAliasesKey = teamAliases.join(",");
  const driverUnreads = useCollectionsCompute(
    driverUnreadCountCollections,
    () =>
      workspaceId && workspaceMembershipId
        ? computeDriverUnreadCount({
            workspaceId,
            workspaceMembershipId,
            teamAliases: teamAliases.length > 0 ? teamAliases : undefined,
          })
        : 0,
    [workspaceId, workspaceMembershipId, teamAliasesKey],
    "LeftSidebar:driverUnreadCount",
  );

  const channelUnreads = useCollectionsCompute(
    channelUnreadCountCollections,
    () =>
      workspaceId && workspaceMembershipId ? computeChannelUnreadCount({ workspaceId, workspaceMembershipId }) : 0,
    [workspaceId, workspaceMembershipId],
    "LeftSidebar:channelUnreadCount",
  );

  const unreads = useMemo(() => {
    return {
      drivers: driverUnreads > 0,
      channels: channelUnreads > 0,
    };
  }, [driverUnreads, channelUnreads]);

  // The workspace's own switch, read from its compiled config; member settings play no part.
  const dispatchEnabled = useDispatchEnabled(workspaceId);

  // Hiding the toggle unmounts the only control for Electron's second window, whose own cleanup closes only the web
  // window. Close it here when Dispatch turns on, not on unmount: the sidebar also remounts on ordinary navigation.
  useEffect(() => {
    if (dispatchEnabled) {
      ipcRendererSend("second-window-close");
    }
  }, [dispatchEnabled]);

  const dispatchItem: VerticalLinkItem = {
    label: "Dispatch",
    url: `/workspaces/${workspaceId}/dispatch`,
    id: "dispatch",
    icon: <InboxIcon />,
    active: pathName.includes("/dispatch"),
    // The same drivers-unread signal: Dispatch works the same alias feeds the driver view does.
    hasUnreads: unreads.drivers,
  };

  const channelsItem: VerticalLinkItem = {
    label: "Channels",
    url: channelsLink ?? `/workspaces/${workspaceId}/channels`,
    id: "channels",
    icon: <ChannelDial />,
    active: pathName.includes("/channels/") || subPage === "channels",
    hasUnreads: unreads.channels,
    onClick: () => ampli.driverTable(),
  };

  const driversItem: VerticalLinkItem = {
    label: "Drivers",
    url: driversLink ?? `/workspaces/${workspaceId}/drivers`,
    id: "drivers",
    icon: <LocalShipping />,
    active: pathName.includes("/drivers/") || subPage === "drivers",
    hasUnreads: unreads.drivers,
    onClick: () => ampli.driverTable(),
  };

  // Typed here rather than on the mapped result so the mix of prebuilt items and literals reads as one item type.
  const topLinkItems: VerticalLinkItem[] = [
    // Dispatch is the driver list's successor: a workspace that turned it on sees it in the driver list's place.
    dispatchEnabled ? dispatchItem : driversItem,
    ...(showChannelsNavItem ? [channelsItem] : []),
    {
      icon: <CalendarMonthIcon />,
      label: "Scheduled Messages",
      url: `/workspaces/${workspaceId}/scheduled-messages`,
      id: "scheduled-messages",
      onClick: () => ampli.scheduledMessages(),
    },
    ...(!limitedMember
      ? [
          {
            label: "Templates",
            url: `/workspaces/${workspaceId}/manage-templates`,
            id: "manage-templates",
            icon: <TemplateIcon />,
            onClick: () => ampli.templates(),
          },
        ]
      : []),
    ...(workspaceAdmin
      ? [
          {
            label: "Manage Users",
            url: `/workspaces/${workspaceId}/manage-users`,
            icon: <ManageUsers />,
            id: "manage-users",
            onClick: () => ampli.manageUsers(),
          },
        ]
      : []),
  ];
  const topLinks: VerticalLinkItem[] = topLinkItems
    .filter((link) => link.url)
    .map((link) => ({
      ...link,
      active: link.active || (subPage && subPage === link.id),
    }));

  const readOnlyModeButton = readOnlyMode
    ? {
        label: "Read Only Mode",
        component: <ReadOnlyModeToggle type="button" setActive={(value) => setReadOnlyActive(value)} />,
        id: "read-only-mode",
        active: readOnlyActive,
      }
    : null;

  const handleNotificationClick = async () => {
    if (notificationsState === "denied") {
      setNotificationsDeniedOpen(true);
      return;
    }
    await requestDesktopNotifications?.();
  };

  const notificationButton: VerticalLinkItem | null =
    !isElectron && notificationsState !== "granted"
      ? {
          label: notificationsState === "denied" ? "Notifications Blocked" : "Enable Notifications",
          icon: notificationsState === "denied" ? <NotificationsOffIcon /> : <NotificationsIcon />,
          id: "enable-notifications",
          onClick: handleNotificationClick,
          bgSx:
            notificationsState === "denied"
              ? { bgcolor: "#dc2626", "&:hover": { bgcolor: "#b91c1c" } }
              : { bgcolor: "#2563eb", "&:hover": { bgcolor: "#1d4ed8" } },
        }
      : null;

  const bottomLinks: VerticalLinkItem[] = [
    notificationButton,
    {
      label: "Search Workspace",
      icon: <Icons.Search role="img" />,
      id: "search-workspace",
      onClick: () => {
        ampli.searchClick({ workspaceId });
        setGlobalSearchOpen(true);
      },
    },
    // Product decision (WEB-2356): the second window is not offered alongside the new dispatch view, so the toggle is
    // hidden on the same gate that swaps Drivers for Dispatch.
    dispatchEnabled
      ? null
      : {
          label: "Activate Second Window",
          component: <ToggleSecondWindowButton />,
          id: "activate-second-window",
        },
    {
      label: "Language",
      component: <LanguageDropButton />,
      id: "language",
      onClick: () => ampli.languageModal(),
    },
    {
      label: "Need Help?",
      icon: <Icons.Help role="presentation" />,
      onClick: () => setHelpOpen(true),
      id: "need-help",
    },
    readOnlyModeButton,
    {
      label: "Your Profile",
      icon: <AvatarWithStatus accountId={myAccount?.id} />,
      id: "your-profile",
      url: `/workspaces/${workspaceId}/your-profile`,
      active: subPage === "your-profile",
      onClick: () => ampli.userSettings(),
    },
  ].filter((link): link is NonNullable<typeof link> => !!link);

  return {
    topLinks,
    bottomLinks,
    helpOpen,
    closeHelp: () => setHelpOpen(false),
    notificationsDeniedOpen,
    closeNotificationsDenied: () => setNotificationsDeniedOpen(false),
  };
}
